import React from 'react';
import ProgressBar from 'react-bootstrap/lib/ProgressBar';
import { connect } from 'react-redux';
import { getData } from 'pages/store/actions/data';
import { getValues, getErrors } from 'pages/store/selectors';
import { actionTypes } from 'pages/store/actions/defaults';

import get from 'lodash/get';

import { Alert } from '@lightspeed/flame/Alert';
import { Button } from '@lightspeed/flame/Button';
import Card, { CardSection } from '@lightspeed/cirrus-card';
import { IconEcomAm } from '@lightspeed/flame/Icon/EcomAm';
import Text from '@lightspeed/cirrus-text';

import Preparing from './Steps/Preparing';
import Building from './Steps/Building';
import Uploading from './Steps/Uploading';
import Completed from './Steps/Completed';

const merchantos = window.merchantos;

const StepComponents = {
  Preparing,
  Building,
  Uploading,
  Completed,
};

const STATUS_QUEUED = 'queued';
const STATUS_CREATED = 'created';
const STATUS_COMPLETED = 'completed';
const STATUS_FAILED = 'failed';

const mapStatusToStep = {
  [STATUS_QUEUED]: 'Building',
  [STATUS_CREATED]: 'Uploading',
  [STATUS_COMPLETED]: 'Completed',
};

const stepMinAnimationTimeout = {
  Preparing: 1400,
  Building: 2200,
  Uploading: 2600,
};

const ecomLink = '/admin/ecom/redirect';

function updateEcomSidebarLink() {
  window.ecomLink = ecomLink;
}

class ProvisioningConfirmation extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      status: 'Preparing',
    };

    this.percentagePerStep = 100 / (Object.keys(StepComponents).length - 1);
  }

  componentDidMount() {
    merchantos.asyncSubscribe.notifySubscribers('react.mount.topbar', {
      props: {
        crumbs: [
          {
            icon: 'ecom',
            text: 'Installing Lightspeed eCom',
          },
        ],
      },
    });

    this.getProvisioningStatus(true);
  }

  componentDidUpdate() {
    const { status } = this.state;
    const isCreated = status === mapStatusToStep[STATUS_CREATED];
    const isCompleted = status === mapStatusToStep[STATUS_COMPLETED];

    if (!this.isEcomSidebarLinkUpdated && (isCreated || isCompleted)) {
      updateEcomSidebarLink();
      this.isEcomSidebarLinkUpdated = true;
    }
  }

  getProvisioningStatus(isInitialRequest = false) {
    const { status } = this.state;
    const { loadingErrors } = this.props;

    this.props.getData('/admin/ecom/provisioning', response => {
      const { status: responseStatus } = response.data.ecomProvisioning;
      const isAlreadyProvisioned = isInitialRequest && responseStatus === STATUS_COMPLETED;

      if (isAlreadyProvisioned) {
        this.setState({
          status: mapStatusToStep[STATUS_COMPLETED],
        });
        return;
      }

      this.setAnimationStep(responseStatus);

      if (mapStatusToStep[STATUS_COMPLETED] !== status && !loadingErrors) {
        setTimeout(() => {
          this.getProvisioningStatus();
        }, 500);
      }
    });
  }

  setAnimationStep(responseStatus) {
    const { status, isAnimationCompleted, isTimeoutStarted } = this.state;
    const stepTimeout = stepMinAnimationTimeout[status];
    const isAborted = status !== null && responseStatus === null;
    const isAbleToSetNextStep = responseStatus && mapStatusToStep[responseStatus] !== status;

    if (!stepTimeout) return;
    if (responseStatus === STATUS_FAILED || isAborted) {
      this.setState({
        isError: true,
      });
      return;
    }

    // If the gif timeout is completed, go directly to the next step
    if (isAnimationCompleted && isAbleToSetNextStep) {
      this.setNextAnimationStep();
    } else if (!isTimeoutStarted) {
      this.setState(
        {
          isTimeoutStarted: true,
        },
        () => {
          // This timeout is used to prevent the gif from jumping to the next frame until its
          // animation is completed. Each gifs have different animation timings therefore a
          // stepMinAnimationTimeout configuration object.
          setTimeout(() => {
            if (!isAnimationCompleted) {
              this.setState({
                isAnimationCompleted: true,
              });
            }
          }, stepTimeout);
        }
      );
    }
  }

  setNextAnimationStep() {
    const { status } = this.state;

    this.setState({
      status: Object.keys(StepComponents)[Object.keys(StepComponents).indexOf(status) + 1],
      isAnimationCompleted: false,
      isTimeoutStarted: false,
    });
  }

  renderProgressBar() {
    const { percentage } = this.props.ecomProvisioning;
    const { status, isError } = this.state;
    const currentStepIndex = Object.keys(StepComponents).indexOf(status);

    let progressBarPercentage = 0;
    const stepPercentage = parseInt(percentage, 10);
    if (!isError) {
      progressBarPercentage =
        this.percentagePerStep * currentStepIndex +
        (stepPercentage ? (this.percentagePerStep / 100) * stepPercentage : 0);
    }

    return <ProgressBar bsStyle="info" className="cr-mt-3" now={progressBarPercentage} />;
  }

  render() {
    const { status, isError } = this.state;
    const { loadingErrors } = this.props;
    const isCreated = status === mapStatusToStep[STATUS_CREATED];
    const isCompleted = status === mapStatusToStep[STATUS_COMPLETED];

    return (
      <div className="react-page__ecom-provisioning cr-p-4 cr-sans-serif cirrus">
        <div className="cr-max-width-content cr-display-block-centered">
          {(loadingErrors || isError) && (
            <div className="cr-mb-2">
              <Alert type="danger" noCloseBtn>
                <p className="cr-m-0">
                  We weren&apos;t able to set up your new eCom store. Don&apos;t worry - contact our
                  Support Team and we&apos;ll get things straightened out.
                </p>
              </Alert>
            </div>
          )}
          <Text body bold>
            {isCompleted ? 'Installation complete.' : "We're installing your online store."}
          </Text>
          <Text body className="cr-pb-4">
            {isCreated || isCompleted
              ? 'Click below to access your eCom Back Office.'
              : "You'll get access to your eCom Back Office in a few moments."}
          </Text>

          <Card large>
            <CardSection>
              <div className="cr-text-center">
                {Object.keys(StepComponents).map(component => {
                  const Component = StepComponents[component];
                  return <Component show={component === status} key={component} />;
                })}

                {!isCompleted && this.renderProgressBar()}

                <div className="cr-mt-4">
                  <Button
                    variant="secondary"
                    fill
                    size="large"
                    href={ecomLink}
                    disabled={!(isCreated || isCompleted)}
                    style={{ color: '#fff' }}
                  >
                    <IconEcomAm />
                    Go to your eCom Back Office
                  </Button>
                </div>
              </div>
            </CardSection>
          </Card>

          {!isCompleted && !loadingErrors ? (
            <Text body className="cr-text-center cr-pt-3">
              You can safely navigate away from this page.
              <br />
              We&apos;ll email you when the installation is completed.
            </Text>
          ) : null}
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  ecomProvisioning: get(getValues('ecomConfirmation')(state), 'ecomProvisioning', {}),
  loadingErrors: getErrors({ entity: 'ecomConfirmation', actionType: actionTypes.LOADING })(state),
});

const mapDispatchToProps = dispatch => ({
  getData: (url, onSuccess) => dispatch(getData({ url, entity: 'ecomConfirmation', onSuccess })),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProvisioningConfirmation);
